import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { cn } from '@/lib/utils';

// Wireframe bus baked by src/lib/dev/scripts/bake-bus-dots.ts: the chiva's
// structural strokes (silhouette + crease lines, simplified and axis-
// snapped) with a draw-on entrance — segments trace themselves in a sweep
// along the bus, each with a bright drawing head that cools down to the
// brand color. Hovering switches to an energy pulse radiating along the
// wires from the point under the cursor. Click to replay the draw-on.
const DOTS_URL = '/assets/models/bus-dots.bin';
const DOTS_MAGIC = 'BDT5';
const DOTS_HEADER_SIZE = 32;
const DOTS_STRIDE = 8;
const REVEAL_SECONDS = 3.2;
const DRAW_WINDOW = 0.22;
const AUTO_PULSE_DELAY_SECONDS = 5;
const AUTO_PULSE_DURATION_SECONDS = 2.6;

type WireBusProps = {
  className?: string;
};

type ParsedSegments = {
  positions: Float32Array;
  inner: Float32Array;
  strong: Float32Array;
};

function parseSegments(buffer: ArrayBuffer): ParsedSegments {
  const view = new DataView(buffer);
  const magic = String.fromCharCode(
    view.getUint8(0),
    view.getUint8(1),
    view.getUint8(2),
    view.getUint8(3),
  );

  if (magic !== DOTS_MAGIC) throw new Error(`bad bus-dots magic: ${magic}`);

  const dotCount = view.getUint32(4, true);
  const bboxMin = [
    view.getFloat32(8, true),
    view.getFloat32(12, true),
    view.getFloat32(16, true),
  ];
  const bboxMax = [
    view.getFloat32(20, true),
    view.getFloat32(24, true),
    view.getFloat32(28, true),
  ];

  const segBase = DOTS_HEADER_SIZE + dotCount * DOTS_STRIDE;
  const segCount = view.getUint32(segBase, true);
  const positions = new Float32Array(segCount * 6);
  const inner = new Float32Array(segCount * 2);
  const strong = new Float32Array(segCount * 2);

  for (let i = 0; i < segCount * 2; i += 1) {
    const off = segBase + 4 + i * 6;

    for (let a = 0; a < 3; a += 1) {
      let uq = view.getInt16(off + a * 2, true) + 32768;
      // First stored vertex: LSB of ax = interior flag, LSB of ay = strong.
      if (i % 2 === 0 && a === 0) {
        inner[i] = uq & 1;
        inner[i + 1] = uq & 1;
        uq &= ~1;
      }
      if (i % 2 === 0 && a === 1) {
        strong[i] = uq & 1;
        strong[i + 1] = uq & 1;
        uq &= ~1;
      }
      positions[i * 3 + a] =
        bboxMin[a] + (uq / 65535) * (bboxMax[a] - bboxMin[a]);
    }
  }

  return { positions, inner, strong };
}

type WireSystem = {
  group: THREE.Group;
  bounds: THREE.Box3;
  update: (
    deltaSeconds: number,
    hovered: boolean,
    pulsePoint: THREE.Vector3 | null,
  ) => void;
  replay: () => void;
};

function buildWireSystem(
  parsed: ParsedSegments,
  targetSize: number,
  cameraDistance: number,
): WireSystem {
  const rawPositions = parsed.positions;
  const sourceBounds = new THREE.Box3().setFromArray(rawPositions);
  const size = sourceBounds.getSize(new THREE.Vector3());
  const center = sourceBounds.getCenter(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z, 1e-6);
  const scale = targetSize / maxDim;

  const vertexCount = rawPositions.length / 3;
  const segmentCount = vertexCount / 2;
  const positions = new Float32Array(rawPositions.length);
  const ts = new Float32Array(vertexCount);
  const delays = new Float32Array(vertexCount);
  const seeds = new Float32Array(vertexCount);

  for (let s = 0; s < segmentCount; s += 1) {
    const ax = (rawPositions[s * 6] - center.x) * scale;
    const ay = (rawPositions[s * 6 + 1] - center.y) * scale;
    const az = (rawPositions[s * 6 + 2] - center.z) * scale;
    const bx = (rawPositions[s * 6 + 3] - center.x) * scale;
    const by = (rawPositions[s * 6 + 4] - center.y) * scale;
    const bz = (rawPositions[s * 6 + 5] - center.z) * scale;
    positions.set([ax, ay, az, bx, by, bz], s * 6);

    // Sweep along the bus length, with jitter so the front never reads as
    // a hard line.
    const midX = (ax + bx) / 2;
    const sweep = (midX / targetSize + 0.5) * 0.85;
    const delay = Math.min(1, Math.max(0, sweep + Math.random() * 0.12));
    const seed = Math.random();
    ts[s * 2] = 0;
    ts[s * 2 + 1] = 1;
    delays[s * 2] = delay;
    delays[s * 2 + 1] = delay;
    seeds[s * 2] = seed;
    seeds[s * 2 + 1] = seed;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('aT', new THREE.BufferAttribute(ts, 1));
  geometry.setAttribute('aDelay', new THREE.BufferAttribute(delays, 1));
  geometry.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 1));
  geometry.setAttribute('aInner', new THREE.BufferAttribute(parsed.inner, 1));
  geometry.setAttribute('aStrong', new THREE.BufferAttribute(parsed.strong, 1));

  const material = new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: new THREE.Color(0xf2ff00) },
      uAccent: { value: new THREE.Color(0xffffff) },
      uTime: { value: 0 },
      uReveal: { value: 0 },
      uCamDist: { value: cameraDistance },
      uDepthHalf: { value: targetSize * 0.45 },
      uXray: { value: 0 },
      uPulseOrigin: { value: new THREE.Vector3(0, 0, 0) },
      uPulseR: { value: -1 },
    },
    vertexShader: `
      attribute float aT;
      attribute float aDelay;
      attribute float aSeed;
      attribute float aInner;
      attribute float aStrong;
      varying float vT;
      varying float vDelay;
      varying float vSeed;
      varying float vFade;
      varying float vPulse;
      uniform float uCamDist;
      uniform float uDepthHalf;
      uniform float uXray;
      uniform vec3 uPulseOrigin;
      uniform float uPulseR;

      void main() {
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_Position = projectionMatrix * mvPosition;
        float front = smoothstep(
          uCamDist + uDepthHalf,
          uCamDist - uDepthHalf,
          -mvPosition.z
        );
        // Long structural strokes carry the shape: render them brighter.
        float shellFade = mix(0.3, 1.0, front) * mix(0.7, 1.5, aStrong);
        // Interior lines get one flat dim factor — stacking the depth fade
        // on top of the interior dim made the seats invisible.
        vFade = mix(shellFade, 0.6, aInner);
        vT = aT;
        vDelay = aDelay;
        vSeed = aSeed;

        // Hover energy pulse: a bright wave expanding along the wires from
        // the point under the cursor.
        float pulseDist = distance(position, uPulseOrigin);
        float band = 1.0 - smoothstep(0.0, 0.22, abs(pulseDist - uPulseR));
        vPulse = band * uXray;
      }
    `,
    fragmentShader: `
      varying float vT;
      varying float vDelay;
      varying float vSeed;
      varying float vFade;
      varying float vPulse;
      uniform vec3 uColor;
      uniform vec3 uAccent;
      uniform float uTime;
      uniform float uReveal;
      uniform float uXray;

      void main() {
        float draw = clamp(
          (uReveal * (1.0 + ${DRAW_WINDOW}) - vDelay) / ${DRAW_WINDOW},
          0.0,
          1.0
        );
        if (vT > draw) discard;

        // Bright white head while the segment is being traced, cooling to
        // the brand color once complete; a soft shimmer keeps it alive.
        float head = draw < 1.0 ? smoothstep(draw - 0.25, draw, vT) : 0.0;
        float shimmer = 0.85 + 0.15 * sin(uTime * 1.6 + vSeed * 6.2831);
        vec3 color = mix(uColor, uAccent, max(head, vPulse * 0.85));
        float alpha =
          (0.55 * shimmer * mix(1.0, 1.5, uXray) + head * 0.85) * vFade +
          vPulse * 0.5;
        gl_FragColor = vec4(color, alpha);
      }
    `,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });

  const group = new THREE.Group();
  const wires = new THREE.LineSegments(geometry, material);
  wires.frustumCulled = false;
  group.add(wires);

  const bounds = new THREE.Box3(
    sourceBounds.min.clone().sub(center).multiplyScalar(scale),
    sourceBounds.max.clone().sub(center).multiplyScalar(scale),
  );

  let elapsed = 0;
  let revealStart = 0;
  let xray = 0;
  let pulseElapsed = 0;
  const pulsePeriod = 1.3;
  const pulseMaxRadius = targetSize * 1.15;

  return {
    group,
    bounds,
    update: (deltaSeconds, hovered, pulsePoint) => {
      elapsed += deltaSeconds;
      material.uniforms.uTime.value = elapsed;
      material.uniforms.uReveal.value = Math.min(
        1,
        (elapsed - revealStart) / REVEAL_SECONDS,
      );

      xray += ((hovered ? 1 : 0) - xray) * Math.min(1, deltaSeconds * 6);
      material.uniforms.uXray.value = xray;

      if (xray > 0.02) {
        pulseElapsed += deltaSeconds;
        if (pulsePoint) {
          material.uniforms.uPulseOrigin.value.copy(pulsePoint);
        }
        material.uniforms.uPulseR.value =
          ((pulseElapsed % pulsePeriod) / pulsePeriod) * pulseMaxRadius;
      } else {
        pulseElapsed = 0;
        material.uniforms.uPulseR.value = -1;
      }
    },
    replay: () => {
      revealStart = elapsed;
    },
  } satisfies WireSystem;
}

function getProjectedBounds(
  box: THREE.Box3,
  object: THREE.Object3D,
  camera: THREE.PerspectiveCamera,
  widthCss: number,
  heightCss: number,
) {
  const corners = [
    new THREE.Vector3(box.min.x, box.min.y, box.min.z),
    new THREE.Vector3(box.min.x, box.min.y, box.max.z),
    new THREE.Vector3(box.min.x, box.max.y, box.min.z),
    new THREE.Vector3(box.min.x, box.max.y, box.max.z),
    new THREE.Vector3(box.max.x, box.min.y, box.min.z),
    new THREE.Vector3(box.max.x, box.min.y, box.max.z),
    new THREE.Vector3(box.max.x, box.max.y, box.min.z),
    new THREE.Vector3(box.max.x, box.max.y, box.max.z),
  ];

  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  for (const corner of corners) {
    corner.applyMatrix4(object.matrixWorld);
    corner.project(camera);
    const sx = (corner.x + 1) * 0.5 * widthCss;
    const sy = (1 - corner.y) * 0.5 * heightCss;
    minX = Math.min(minX, sx);
    maxX = Math.max(maxX, sx);
    minY = Math.min(minY, sy);
    maxY = Math.max(maxY, sy);
  }

  return { minX, minY, maxX, maxY };
}

export function WireBus({ className }: WireBusProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 100);
    camera.position.set(2, 1.1, 2.8);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    let wireSystem: WireSystem | null = null;
    let disposed = false;
    let isHovered = false;
    let sceneElapsed = 0;
    let autoPulseTriggered = false;
    const pointer = { x: 0, y: 0, active: false };
    const raycaster = new THREE.Raycaster();
    const pointerNdc = new THREE.Vector2();
    const localRay = new THREE.Ray();
    const inverseGroupMatrix = new THREE.Matrix4();
    const pulseHit = new THREE.Vector3();

    const onPointerMove = (event: PointerEvent) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = event.clientX - rect.left;
      pointer.y = event.clientY - rect.top;
      pointer.active = true;
    };

    const onPointerLeave = () => {
      pointer.active = false;
      isHovered = false;
    };

    const restart = () => {
      wireSystem?.replay();
      sceneElapsed = 0;
      autoPulseTriggered = false;
    };

    renderer.domElement.addEventListener('pointermove', onPointerMove);
    renderer.domElement.addEventListener('pointerleave', onPointerLeave);
    window.addEventListener('pointerdown', restart);

    const isMobileViewport = window.matchMedia('(max-width: 767px)').matches;
    const targetModelSize = isMobileViewport ? 1.45 : 2.0;

    fetch(DOTS_URL)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.arrayBuffer();
      })
      .then((buffer) => {
        if (disposed) return;

        wireSystem = buildWireSystem(
          parseSegments(buffer),
          targetModelSize,
          camera.position.length(),
        );
        wireSystem.group.rotation.y = Math.PI / 6;
        scene.add(wireSystem.group);
      })
      .catch((error) => {
        console.error('Failed to load bus wireframe', error);
      });

    const resize = () => {
      const width = Math.max(1, container.clientWidth);
      const height = Math.max(1, container.clientHeight);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };

    resize();
    window.addEventListener('resize', resize);

    const clock = new THREE.Clock();
    const animate = () => {
      const delta = Math.min(0.1, clock.getDelta());
      sceneElapsed += delta;

      if (!autoPulseTriggered && sceneElapsed >= AUTO_PULSE_DELAY_SECONDS) {
        autoPulseTriggered = true;
      }
      const autoPulseActive =
        autoPulseTriggered &&
        sceneElapsed < AUTO_PULSE_DELAY_SECONDS + AUTO_PULSE_DURATION_SECONDS;

      if (wireSystem) {
        wireSystem.group.updateMatrixWorld(true);
        const rect = renderer.domElement.getBoundingClientRect();
        const { minX, minY, maxX, maxY } = getProjectedBounds(
          wireSystem.bounds,
          wireSystem.group,
          camera,
          rect.width,
          rect.height,
        );
        const padding = 3;
        const pointerHovered =
          pointer.active &&
          pointer.x >= minX - padding &&
          pointer.x <= maxX + padding &&
          pointer.y >= minY - padding &&
          pointer.y <= maxY + padding;
        isHovered = pointerHovered || autoPulseActive;

        let pulsePoint: THREE.Vector3 | null = null;

        if (pointerHovered && rect.width > 0 && rect.height > 0) {
          pointerNdc.set(
            (pointer.x / rect.width) * 2 - 1,
            -(pointer.y / rect.height) * 2 + 1,
          );
          raycaster.setFromCamera(pointerNdc, camera);
          inverseGroupMatrix.copy(wireSystem.group.matrixWorld).invert();
          localRay.copy(raycaster.ray).applyMatrix4(inverseGroupMatrix);
          pulsePoint = localRay.intersectBox(wireSystem.bounds, pulseHit);
        } else if (autoPulseActive) {
          pulsePoint = wireSystem.bounds.getCenter(pulseHit);
        }

        wireSystem.group.rotation.y += delta * 0.55;
        wireSystem.update(delta, isHovered, pulsePoint);
      }

      renderer.render(scene, camera);
    };

    // Only burn GPU while the hero is actually on screen and the tab visible.
    let isInView = true;
    let isPageVisible = !document.hidden;

    const syncAnimationLoop = () => {
      renderer.setAnimationLoop(isInView && isPageVisible ? animate : null);
    };

    const intersectionObserver = new IntersectionObserver((entries) => {
      isInView = entries.some((entry) => entry.isIntersecting);
      syncAnimationLoop();
    });
    intersectionObserver.observe(container);

    const onVisibilityChange = () => {
      isPageVisible = !document.hidden;
      syncAnimationLoop();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    syncAnimationLoop();

    return () => {
      disposed = true;
      intersectionObserver.disconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      renderer.domElement.removeEventListener('pointermove', onPointerMove);
      renderer.domElement.removeEventListener('pointerleave', onPointerLeave);
      window.removeEventListener('pointerdown', restart);
      window.removeEventListener('resize', resize);
      renderer.setAnimationLoop(null);
      renderer.dispose();

      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div ref={containerRef} className={cn('absolute inset-0', className)} />
  );
}
